import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { listContexts } from "@/api/agent";
import ContextPicker from "@/components/agent/ContextPicker";
import HelpLink, { GUIDE_URLS } from "@/components/HelpLink";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const AgentChat = lazy(() => import("@/components/agent/AgentChat"));

export default function AssistantPanel({
  token,
  open,
  onOpenChange,
  contextName,
  onContextChange,
  onConversationOpened,
  returnFocusTo,
}) {
  const [mounted, setMounted] = useState(open);
  const [contexts, setContexts] = useState([]);
  const [persona, setPersona] = useState("unknown");
  const asideRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  // Les modes se chargent à chaque ouverture tant qu'ils n'ont pas été obtenus :
  // un échec réseau à la première ouverture ne laisse pas le panneau vide toute
  // la session, et il est signalé (comme l'ancienne page le faisait).
  const [contextsLoaded, setContextsLoaded] = useState(false);
  useEffect(() => {
    if (!mounted || !open || contextsLoaded) return undefined;
    let alive = true;
    listContexts(token)
      .then((data) => {
        if (!alive) return;
        setContexts(data.contexts || []);
        setPersona(data.persona || "unknown");
        setContextsLoaded(true);
      })
      .catch((error) => {
        if (!alive) return;
        console.error(error);
        toast.error("Impossible de charger l'assistant");
      });
    return () => {
      alive = false;
    };
  }, [mounted, open, contextsLoaded, token]);

  useEffect(() => {
    if (!contextsLoaded || !contextName) return;
    if (!contexts.some((context) => context.name === contextName)) {
      onContextChange(null);
    }
  }, [contextName, contexts, contextsLoaded, onContextChange]);

  useEffect(() => {
    if (!open || !asideRef.current) return undefined;

    const aside = asideRef.current;
    let observer;
    const focusTarget = () => {
      const target = aside.querySelector("textarea")
        || aside.querySelector("button.agent-context-card");
      if (!target) return false;
      target.focus();
      observer?.disconnect();
      return true;
    };

    if (focusTarget()) return undefined;

    observer = new MutationObserver(focusTarget);
    observer.observe(aside, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open, contextName, mounted]);

  const close = () => {
    onOpenChange(false);
    returnFocusTo?.current?.focus?.();
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
    }
  };

  const goMyRequests = () => {
    navigate("/mes-demandes");
    if (window.innerWidth < 640) onOpenChange(false);
  };

  if (!mounted) return null;

  return (
    <aside
      ref={asideRef}
      id="assistant-panel"
      aria-label="Assistant"
      hidden={!open}
      onKeyDown={onKeyDown}
      // `hidden` (attribut) ne suffit pas : la classe `flex` gagne sur la règle
      // preflight `[hidden]` dans le CSS livré. La classe pilote l'affichage.
      className={cn(
        "fixed right-0 z-30 w-full flex-col border-l bg-card shadow-xl sm:w-[440px]",
        open ? "flex" : "hidden"
      )}
      style={{ top: "var(--shell-h)", height: "calc(100vh - var(--shell-h))" }}
    >
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <span className="font-medium">Assistant</span>
        <select
          aria-label="Mode"
          className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-sm"
          value={contextName || ""}
          onChange={(event) => onContextChange(event.target.value || null)}
        >
          <option value="">Choisir un mode…</option>
          {contexts.map((context) => (
            <option key={context.name} value={context.name}>
              {context.label}
              {context.status === "completed"
                ? " · ✓ Terminé"
                : context.n_questions > 0
                  ? ` · ${context.n_answered || 0}/${context.n_questions}`
                  : ""}
            </option>
          ))}
        </select>
        <HelpLink url={GUIDE_URLS.assistant} />
        <Button
          variant="ghost"
          size="icon"
          aria-label="Fermer l'assistant"
          onClick={close}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {contextName ? (
          <Suspense fallback={<p className="p-4 text-sm text-muted-foreground">Chargement de l'assistant…</p>}>
            <AgentChat
              token={token}
              contextName={contextName}
              onConversationOpened={onConversationOpened}
            />
          </Suspense>
        ) : (
          <ContextPicker
            compact
            contexts={contexts}
            persona={persona}
            onPick={onContextChange}
            onMyRequests={goMyRequests}
          />
        )}
      </div>
    </aside>
  );
}
